import * as React from "react";
import type { RecipeStep, CookSessionApiItem } from "@recipevault/shared";
import { Button } from "../../ui/button.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PrepPanelProps {
  steps: RecipeStep[];
  stepStates: CookSessionApiItem["stepStates"];
  onToggleDone: (stepId: string) => void;
}

// ---------------------------------------------------------------------------
// PrepPanel
// ---------------------------------------------------------------------------

/**
 * Mise-en-place checklist (spec §6.4).
 *
 * - If no prep steps → renders nothing.
 * - Starts expanded. While any prep step is undone → shows the checklist.
 * - Once ALL prep steps are done → auto-collapses to a thin summary bar.
 *   Tapping the bar re-expands (so the user can un-check a prep item).
 */
export function PrepPanel({ steps, stepStates, onToggleDone }: PrepPanelProps) {
  // Filter to only prep-kind steps
  const prepSteps = steps.filter((s) => s.kind === "prep");

  // No prep steps → render nothing
  if (prepSteps.length === 0) return null;

  return (
    <PrepPanelInner
      prepSteps={prepSteps}
      stepStates={stepStates}
      onToggleDone={onToggleDone}
    />
  );
}

// ---------------------------------------------------------------------------
// Inner component — holds local expanded state
// We split so the hook runs only when there are prep steps.
// ---------------------------------------------------------------------------

interface PrepPanelInnerProps {
  prepSteps: RecipeStep[];
  stepStates: CookSessionApiItem["stepStates"];
  onToggleDone: (stepId: string) => void;
}

function PrepPanelInner({ prepSteps, stepStates, onToggleDone }: PrepPanelInnerProps) {
  const doneCount = prepSteps.filter(
    (s) => stepStates[s.id]?.status === "done",
  ).length;
  const total = prepSteps.length;
  const allDone = doneCount === total;

  // Start expanded unless all prep steps are already done on first render.
  // When all are done and user hasn't manually re-opened → collapsed bar.
  // When any step is un-done → auto-expand so the remaining items are visible.
  const [expanded, setExpanded] = React.useState(!allDone);

  // If any prep step becomes un-done while collapsed, re-expand automatically
  // so the user always sees the remaining items.
  React.useEffect(() => {
    if (!allDone) {
      setExpanded(true);
    }
  }, [allDone]);

  // Collapsed summary bar — shown only when all done AND user hasn't re-opened
  if (allDone && !expanded) {
    return (
      <div
        role="region"
        aria-label="Prep checklist (collapsed)"
        className="bg-zinc-800 px-4 py-2"
      >
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Prep · ${total}/${total} done`}
          className="h-auto min-h-[48px] w-full justify-start px-0 text-sm font-medium text-emerald-400 hover:bg-transparent hover:text-emerald-300"
          onClick={() => setExpanded(true)}
        >
          Prep · {total}/{total} done
        </Button>
      </div>
    );
  }

  // All done but expanded → user tapped the bar to re-open; show checklist
  // (same as partially-done view, just all rows show checked state)

  // Expanded checklist view
  return (
    <div
      role="region"
      aria-label="Prep checklist"
      className="bg-zinc-800 px-4 py-3"
    >
      {/* Header — tap to collapse when all done */}
      {allDone && (
        <button
          type="button"
          aria-label={`Prep · ${total}/${total} done`}
          onClick={() => setExpanded(false)}
          className="mb-2 flex w-full items-center gap-2 text-sm font-medium text-emerald-400"
        >
          Prep · {total}/{total} done
        </button>
      )}

      {!allDone && (
        <p className="mb-2 text-sm font-semibold text-zinc-100">
          Prep · {doneCount}/{total} done
        </p>
      )}

      {/* Checklist rows */}
      <ul className="flex flex-col gap-1">
        {prepSteps.map((step) => {
          const isDone = stepStates[step.id]?.status === "done";
          return (
            <li key={step.id}>
              <Button
                variant="ghost"
                aria-label={step.text}
                aria-pressed={isDone}
                className="min-h-[48px] w-full justify-start gap-3 px-2 text-left text-base text-zinc-100 hover:bg-zinc-700"
                onClick={() => onToggleDone(step.id)}
              >
                {/* Checkbox indicator */}
                <span
                  aria-hidden
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border-2 transition-colors ${
                    isDone
                      ? "border-emerald-500 bg-emerald-500 text-white"
                      : "border-zinc-500 bg-transparent"
                  }`}
                >
                  {isDone && (
                    <svg
                      viewBox="0 0 12 12"
                      fill="none"
                      className="h-3 w-3"
                      aria-hidden
                    >
                      <path
                        d="M2 6l3 3 5-5"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                {/* Plain text — never innerHTML */}
                <span className={isDone ? "text-zinc-400 line-through" : "text-zinc-100"}>
                  {step.text}
                </span>
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
